









































































import { defineComponent, inject, onMounted, ref } from '@vue/composition-api'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
  faChevronLeft,
  faCommentAlt,
  faExchangeAlt
} from '@fortawesome/free-solid-svg-icons'
import cloneDeep from 'lodash/cloneDeep'
import TextSearch from '../TextSearch.vue'
import KaiButton from '../common/KaiButton.vue'
import { useActions } from '../../store/useActions'
import { useGetters } from '../../store/useGetters'
import { TranscriptionV2ApiKey, Transcript } from '../../transcription.api'
import { TranscriptionManagerApiKey } from '@/rest/transcription-manager-api'
import { SavingSteps } from '../../types/transcript'
import { offload } from '@/lib/async'
import { ConversationsApiKey } from '@/rest/conversations-api'

export default defineComponent({
  name: 'kai-header',
  components: { TextSearch, KaiButton, FontAwesomeIcon },
  props: {
    transcriptId: {
      required: true,
      type: Number
    },

    heading: String,
    title: String,
    language: String
  },
  setup(props, { emit }) {
    const commentIcon = faCommentAlt
    const swapSpeakersIcon = faExchangeAlt
    const backIcon = faChevronLeft
    const edited = ref(false)
    const taggingDisabled = ref(false)
    const taggingStatus = ref({})
    function updateTaggingStatus() {
      localStorage.setItem(
        'tagging_status',
        JSON.stringify(taggingStatus.value)
      )
    }
    function startTagTimer() {
      setTimeout(() => {
        taggingDisabled.value = false
        delete taggingStatus.value[props.transcriptId]
        updateTaggingStatus()
      }, taggingStatus.value[props.transcriptId].expires - Date.now())
    }
    onMounted(() => {
      const taggingStatusLS = localStorage.getItem('tagging_status')
      taggingStatus.value = JSON.parse(taggingStatusLS) || {}
      if (taggingStatus.value && taggingStatus.value[props.transcriptId]) {
        if (taggingStatus.value[props.transcriptId].expires > Date.now()) {
          taggingDisabled.value = true
          startTagTimer()
        } else {
          delete taggingStatus.value[props.transcriptId]
          updateTaggingStatus()
        }
      }
    })

    const transcriptionApi = inject(TranscriptionV2ApiKey)
    const transcriptionManager = inject(TranscriptionManagerApiKey)
    const conversationApi = inject(ConversationsApiKey)

    const getters = useGetters({
      isEditable: 'conversation/isEditable',
      transcript: 'conversation/transcript',
      lastWordDisplayed: 'conversation/lastWordDisplayed',
      transcriptHistory: 'conversation/transcriptHistory',
      tagCategories: 'transcriptTags/categories'
    })

    const actions = useActions({
      toggleIsEditable: 'conversation/toggleIsEditable',
      save: 'conversation/save',
      editorLoading: 'conversation/editorLoading',
      undo: 'conversation/undo',

      editorInit: 'editor/init',
      editorSave: 'editor/save'
    })

    async function genTags() {
      const continue_tagging = confirm(
        'Find Tags needs 2 minutes to complete. This button will be disabled until then.'
      )
      if (continue_tagging) {
        taggingDisabled.value = true
        const tagging = await transcriptionManager.StartTagging(
          props.transcriptId,
          getters.transcript
        )
        if (!tagging.timeStarted) {
          alert(
            'An error occurred with tagging. Your user account must be a KAI Editor to Find Tags.'
          )
          throw tagging
        }
        const expires = new Date(tagging.timeStarted)
        expires.setMinutes(expires.getMinutes() + 2)
        taggingStatus.value[props.transcriptId] = {
          expires: expires.getTime()
        }
        startTagTimer()
        updateTaggingStatus()
      }
    }

    function edit() {
      const tagsEdited = getters.tagCategories.value.length > 0
      let confirmation = false
      if (tagsEdited) {
        confirmation = confirm(
          'Editing text after it has been tagged may cause tags to become misaligned'
        )
      }
      if (tagsEdited == false || confirmation) {
        const switchingToEditMode = offload(() =>
          cloneDeep(getters.transcript.value.parts)
        ).then(parts => {
          actions.editorInit({
            parts,
            version: getters.transcript.value.version
          })
          actions.toggleIsEditable()
        })
        edited.value = true
        emit('edit', switchingToEditMode)
        return switchingToEditMode
      }
    }

    const saveTranscript = async () => {
      let transcript = await actions.editorSave()
      return transcriptionApi.saveTranscriptV3(
        props.transcriptId,
        transcript,
        props.language
      )
    }

    const transcriptCalculations = (transcriptResp: Transcript) => {
      return transcriptionApi.transcriptCalculations(
        transcriptResp.id,
        transcriptResp.version
      )
    }

    const publishTranscript = async (transcriptResp: Transcript) => {
      return transcriptionApi.publishTranscript(
        transcriptResp.id,
        transcriptResp.version,
        true
      )
    }

    const updateStatus = async (transcriptResp: Transcript) => {
      const id = transcriptResp.id
      const conversation = await conversationApi.GetConversation(id)

      if (conversation._tag == 'success') {
        if (conversation.data[0].status !== 'published') {
          return conversationApi.UpdateConversation(id, {
            status: 'committed'
          })
        }
      }
    }

    const clearAutomatedData = async (transcript: Transcript) => {
      return transcriptionApi.clearAutomatedData(
        transcript.id,
        transcript.version,
        true
      )
    }

    const swapSpeakers = () => {
      const confirmed = window.confirm(
        'This will swap the speakers of each part of the transcript. There must be only two speakers for this to succeed. Any unsaved changes made to the transcript will be lost. Are you sure you want to swap speakers?'
      )
      if (!confirmed) return
      const swapSpeakers = transcriptionApi.swapSpeakers(props.transcriptId)
      emit('swapping', swapSpeakers)
    }

    async function save() {
      const savingSteps: SavingSteps = {
        current: {
          method: saveTranscript,
          isResolved: false,
          promise: null,
          working: false,
          waiting: false,
          label: 'Saving transcript',
          success: 'Saved',
          failure: 'Failed',
          publish: false,
          step: 1,
          attempt: 0
        },
        steps: []
      }
      emit('saving', savingSteps)
    }

    async function cancel() {
      const confirmed = window.confirm(
        'Any changes made will not be saved and will be lost. Are you sure?'
      )
      if (!confirmed) return
      const exitingEditMode = offload(() => actions.toggleIsEditable())
      emit('cancelling', exitingEditMode)
      return exitingEditMode
    }

    const publishTranscription = async () => {
      const publishingSteps: SavingSteps = {
        current: {
          method: clearAutomatedData,
          isResolved: false,
          promise: null,
          working: false,
          label: 'Resetting automated data',
          success: 'Data reset',
          failure: 'Failed resetting',
          publish: false,
          step: 1,
          attempt: 0
        },
        steps: [
          {
            method: transcriptCalculations,
            step: 2,
            working: false,
            waiting: true,
            label: 'Performing calculations',
            success: 'Calculated',
            failure: 'Error calculating',
            attempt: 0
          },
          {
            method: publishTranscript,
            step: 2,
            working: false,
            waiting: true,
            label: 'Publishing transcript',
            success: 'Published',
            failure: 'Error publishing',
            attempt: 0
          },
          {
            method: updateStatus,
            step: 2,
            working: false,
            waiting: true,
            label: 'Updating Conversation Status',
            success: 'Updated',
            failure: 'Error updating',
            attempt: 0
          }
        ]
      }
      emit('saving', publishingSteps)
    }

    function undo() {
      actions.undo(true)
    }

    // watch(getters.lastWordDisplayed, () => {
    //     if (getters.lastWordDisplayed.value) {
    //         console.log('stop loading')
    //     }
    // })
    return {
      publishTranscription,
      commentIcon,
      swapSpeakersIcon,
      swapSpeakers,
      genTags,
      backIcon,
      isEditable: getters.isEditable,
      history: getters.transcriptHistory,
      edit,
      save,
      cancel,
      undo,
      taggingDisabled
    }
  }
})
